import { forwardRef } from 'react';
import { Link as RouterLink, type LinkProps } from 'react-router-dom';

/**
 * Append a trailing slash to internal string paths so links point directly at
 * the canonical (trailing-slash) URL and avoid a 301 redirect hop. This keeps
 * Googlebot crawling 200s instead of repeatedly discovering the non-slash
 * variants (which were showing up in GSC as "Redirect error" / "Page with
 * redirect") and preserves crawl budget.
 *
 * Left untouched: external/protocol-relative URLs, the root "/", hash/query
 * links, file paths with an extension, and object `to` values.
 */
export function withTrailingSlash(to: LinkProps['to']): LinkProps['to'] {
  if (typeof to !== 'string') return to;                 // object `to` — leave as-is
  if (!to.startsWith('/') || to.startsWith('//')) return to; // external / protocol-relative
  if (to === '/') return to;                             // root
  if (to.includes('?') || to.includes('#')) return to;   // query / hash present
  const lastSegment = to.split('/').pop() ?? '';
  if (lastSegment.includes('.')) return to;              // looks like a file (has extension)
  return to.endsWith('/') ? to : `${to}/`;
}

const SmartLink = forwardRef<HTMLAnchorElement, LinkProps>(function SmartLink(
  { to, ...rest },
  ref,
) {
  return <RouterLink ref={ref} to={withTrailingSlash(to)} {...rest} />;
});

export { SmartLink as Link };
export default SmartLink;
